import * as React from "react";
import { View, Text } from "react-native";
import Slider from "@react-native-community/slider";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { BottomSheet } from "@/shared/components/ui/bottom-sheet";
import { Button } from "@/shared/components/ui/button";
import { Heading } from "@/shared/components/ui/heading";
import { Pill } from "@/shared/components/ui/pill";

type DistanceSelectProps = {
  value: number;
  onValueChange: (value: number) => void;
};

const MIN_DISTANCE = 5;
const MAX_DISTANCE = 30;

const formatDistance = (distance: number, forButton = false) => {
  if (distance >= MAX_DISTANCE && forButton) {
    return "Localização";
  }
  return `até ${distance} km`;
};

export function DistanceSelect({ value, onValueChange }: DistanceSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [brand, border] = useCSSVariable([
    "--color-brand",
    "--color-border",
  ]) as [string, string];

  const isFilterActive = value < MAX_DISTANCE;

  const handleClear = () => {
    onValueChange(MAX_DISTANCE);
  };

  const handleApply = () => {
    setOpen(false);
  };

  return (
    <>
      <Pill
        label={formatDistance(value, true)}
        variant="soft"
        tone="brand"
        selected={isFilterActive}
        leftIcon={<Ionicons name="location" size={18} color={brand} />}
        rightIcon={<Ionicons name="chevron-down" size={14} color={brand} />}
        minWidth={160}
        onPress={() => setOpen(true)}
        accessibilityLabel={`Distance filter: ${formatDistance(value, true)}`}
        accessibilityHint="Double tap to select maximum distance"
      />

      <BottomSheet visible={open} onClose={handleApply}>
        <View className="px-5 py-6">
          <Heading variant="section" className="mb-6">
            Localização
          </Heading>

          <View className="mb-6 items-center">
            <Text className="text-foreground text-2xl font-bold">
              {formatDistance(value)}
            </Text>
            <Text className="text-muted-foreground mt-1 text-sm">
              Raio de busca
            </Text>
          </View>

          <Slider
            style={{ width: "100%", height: 40 }}
            minimumValue={MIN_DISTANCE}
            maximumValue={MAX_DISTANCE}
            step={5}
            value={value}
            onValueChange={onValueChange}
            minimumTrackTintColor={brand}
            maximumTrackTintColor={border}
            thumbTintColor={brand}
          />

          <View className="mt-2 flex-row justify-between">
            <Text className="text-muted-foreground text-sm">
              {MIN_DISTANCE} km
            </Text>
            <Text className="text-muted-foreground text-sm">
              {MAX_DISTANCE} km
            </Text>
          </View>
        </View>

        <View className="border-border flex-row gap-3 border-t px-5 pt-4">
          <Button
            variant="outline"
            className="flex-1"
            onPress={handleClear}
            accessibilityLabel="Clear distance filter"
          >
            Limpar
          </Button>
          <Button
            className="flex-1"
            onPress={handleApply}
            accessibilityLabel="Apply distance filter"
          >
            Filtrar
          </Button>
        </View>
      </BottomSheet>
    </>
  );
}
